import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import SecureStorage from "../utils/secureStorage";

export const AuthContext = createContext();

// Inactivity timeout: 30 minutes
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const inactivityTimer = useRef(null);

  // ── Logout (centralized) ──────────────────────────────────────────────────
  const logout = useCallback(() => {
    SecureStorage.clearAll();
    setIsAuthenticated(false);
    setIsAdminAuthenticated(false);
    setUser(null);
  }, []);

  // ── Inactivity Timer ──────────────────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    inactivityTimer.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  }, []);

  // ── Mount: restore session from sessionStorage with expiry validation ─────
  useEffect(() => {
    const token = SecureStorage.getToken(false);
    const adminToken = SecureStorage.getToken(true);

    if (token && !SecureStorage.isTokenExpired(token)) {
      const storedUser = SecureStorage.getUser(false);
      setIsAuthenticated(true);
      setUser(storedUser);
    }

    if (adminToken && !SecureStorage.isTokenExpired(adminToken)) {
      const storedAdmin = SecureStorage.getUser(true);
      setIsAdminAuthenticated(true);
      if (!user) setUser(storedAdmin);
    }

    // If nothing valid found, ensure clean state
    if (!token && !adminToken) {
      SecureStorage.clearAll();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inactivity tracking — attach/detach listeners ────────────────────────
  useEffect(() => {
    if (isAuthenticated || isAdminAuthenticated) {
      const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
      events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
      resetInactivityTimer(); // Start the timer immediately

      return () => {
        events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
        clearInactivityTimer();
      };
    } else {
      clearInactivityTimer();
    }
  }, [isAuthenticated, isAdminAuthenticated, resetInactivityTimer, clearInactivityTimer]);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = (token, userData) => {
    if (userData && userData.isAdmin) {
      SecureStorage.setToken(token, true);
      SecureStorage.setUser(userData, true);
      setIsAdminAuthenticated(true);
    } else {
      SecureStorage.setToken(token, false);
      SecureStorage.setUser(userData, false);
      setIsAuthenticated(true);
    }
    // Store only safe fields — SecureStorage.setUser already strips password
    const { password, ...safeUser } = userData || {};
    setUser(safeUser);
  };

  // ── Admin Login ───────────────────────────────────────────────────────────
  const adminLogin = (token, adminData) => {
    SecureStorage.setToken(token, true);
    SecureStorage.setUser(adminData, true);
    setIsAdminAuthenticated(true);
    const { password, ...safeAdmin } = adminData || {};
    setUser(safeAdmin);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAdminAuthenticated,
        user,
        login,
        logout,
        adminLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
